import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  X,
  Ban,
  AlertCircle,
  AlertTriangle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import api from '@/services/api';
import { useAuthStore } from '@/stores/authStore';

interface RevokeModalProps {
  isOpen: boolean;
  onClose: () => void;
  formId: string;
  onSuccess: () => void;
}

export default function RevokeModal({ isOpen, onClose, formId, onSuccess }: RevokeModalProps) {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  const [reason, setReason] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  const revokeMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        reason,
        revokedById: user?.id,
      };

      const response = await api.post(`/access-forms/${formId}/revoke`, payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accessForm', formId] });
      queryClient.invalidateQueries({ queryKey: ['accessForms'] });
      resetForm();
      onSuccess();
    },
    onError: (error: any) => {
      setSubmitError(error.response?.data?.message || 'Failed to revoke access');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!reason.trim()) {
      setSubmitError('Please provide a reason for revoking access');
      return;
    }

    revokeMutation.mutate();
  };

  const resetForm = () => {
    setReason('');
    setSubmitError(null);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-background rounded-lg shadow-lg w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-2 text-orange-600">
            <Ban className="w-5 h-5" />
            <h2 className="text-xl font-semibold">Revoke Access</h2>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Body */}
        <div className="p-6">
          {/* Error Alert */}
          {submitError && (
            <div className="mb-6 p-4 bg-destructive/10 border border-destructive rounded-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-destructive" />
              <p className="text-destructive">{submitError}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
              <div className="flex items-center gap-2 text-orange-700">
                <AlertTriangle className="w-5 h-5" />
                <p className="font-medium">Revoke Employee Access</p>
              </div>
              <p className="text-sm text-orange-600 mt-1">
                This action will revoke all access that was provisioned for this employee. 
                This is typically done when an employee leaves the company, changes roles, or 
                when access is no longer needed.
              </p>
            </div>

            {/* Revocation Reason */}
            <div className="space-y-2">
              <Label htmlFor="reason">
                Reason for Revocation <span className="text-destructive">*</span>
              </Label>
              <textarea
                id="reason"
                className={cn(
                  "w-full min-h-[120px] p-3 rounded-md border bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring",
                  !reason.trim() && submitError ? "border-destructive focus:ring-destructive" : "border-input"
                )}
                placeholder="Please explain why access is being revoked (e.g., employee resignation, role change, security concern)..."
                value={reason}
                onChange={(e) => {
                  setReason(e.target.value);
                  if (submitError) setSubmitError(null);
                }}
              />
            </div>

            <div className="p-3 bg-muted rounded-lg">
              <h4 className="font-medium text-sm mb-2">After Revocation</h4>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li>• All system access will be removed</li>
                <li>• Employee will lose access to provisioned systems</li>
                <li>• This action will be recorded in the audit log</li>
                <li>• The access form status will change to "Revoked"</li>
              </ul>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-4 p-6 border-t">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={revokeMutation.isPending}
            loading={revokeMutation.isPending}
            className="bg-orange-600 hover:bg-orange-700"
          >
            <Ban className="w-4 h-4 mr-2" />
            Revoke Access
          </Button>
        </div>
      </div>
    </div>
  );
}
